import { CreationDateSortOptions, TimestampSortOptions } from "src/enums";
import { CreatedAtDateTimeRange, DateTimeRange } from "../types";

export namespace DD_MM_YYYY {
  export function formatDate(date: Date | string | number): string | undefined {
    if (!date) {
      return;
    }
    const pattern = "DD/MM/YYYY";
    const formattedDate = new Date(date);
    const day = String(formattedDate.getDate()).padStart(2, "0");
    const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
    const year = formattedDate.getFullYear();
    return pattern.replace("DD", day).replace("MM", month).replace("YYYY", String(year));
  }

  export function formatDateAndAppendTime(date: Date | string): string | undefined {
    return date ? DD_MM_YYYY.formatDate(date) + ", " + new Date(date).toLocaleTimeString() : undefined;
  }

  export function isInvalidDate(date: string): boolean {
    const dateRegex: RegExp = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRegex.test(date)) {
      return true;
    }
    const parts = date.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (month < 1 || month > 12) {
      return true;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return true;
    }

    return false;
  }
}

export function isoDateLocal(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function timeFormat(date: Date | string | number): string {
  const pattern = "hh:mm:ss";
  const formattedDate = new Date(date);
  const hours = String(formattedDate.getHours()).padStart(2, "0");
  const minutes = String(formattedDate.getMinutes()).padStart(2, "0");
  const seconds = String(formattedDate.getSeconds()).padStart(2, "0");
  return pattern.replace("hh", hours).replace("mm", minutes).replace("ss", seconds);
}

export function dateTimeRange(startDateTime?: string, endDateTime?: string): DateTimeRange {
  const dateTimeRange: DateTimeRange = {};

  if (startDateTime) {
    dateTimeRange.start = new Date(startDateTime);
  }

  if (endDateTime) {
    dateTimeRange.end = new Date(endDateTime);
  }

  return dateTimeRange;
}

export function areDatesEqualWithinMinutes(date1: Date, date2: Date, minutes: number): boolean {
  const millisecondsDiff = Math.abs(date1.getTime() - date2.getTime());
  const minutesDiff = millisecondsDiff / (1000 * 60);
  return Math.abs(minutesDiff - minutes) < 1;
}

export function isInvalidISODate<T>(date: T): boolean {
  if (!date || typeof date !== "string" || date.trim() === "") {
    return true;
  }

  const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?$/;
  if (!isoRegex.test(date)) {
    return true;
  }

  const [year, month, day] = date.split("T")[0].split("-").map(Number);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return true;
  }

  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return (
    parsedDate.getUTCFullYear() !== year || parsedDate.getUTCMonth() !== month - 1 || parsedDate.getUTCDate() !== day
  );
}

export function monthsAgo(months: number): Date {
  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - months);
  return currentDate;
}

export function isDateInTheFuture(date: string | Date): boolean {
  return new Date(date) > new Date();
}

export function parseDDMMYYYYToISO(value: unknown): string | null {
  if (value == null || typeof value !== "string") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (DD_MM_YYYY.isInvalidDate(raw)) return null;
  const [d, m, y] = raw.split("/").map(Number);
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function isDateTodayOrInTheFuture(date: string | Date): boolean {
  const inputDate = new Date(date);
  const today = new Date();

  // Remove the time part
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return inputDate >= today;
}

export function incrementDateByDays(date: Date, days: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

// default date options (isomorphic with server-side)

export function defaultStartDateRangeDate(): Date {
  const defaultStartDateRangeDate = monthsAgo(4);
  defaultStartDateRangeDate.setHours(0, 0, 0, 0);
  return defaultStartDateRangeDate;
}

export function defaultMaxStartDateRangeDate(): Date {
  const defaultMaxStartDateRangeDate = monthsAgo(12);
  defaultMaxStartDateRangeDate.setHours(0, 0, 0, 0);
  return defaultMaxStartDateRangeDate;
}

export function defaultDateRange(): DateTimeRange {
  return { start: defaultStartDateRangeDate(), end: undefined };
}

export const defaultCreatedAtDateTimeRange = (): CreatedAtDateTimeRange => {
  return { createdAtStart: defaultStartDateRangeDate().toISOString() };
};

export function currentDayDateTimeRange(): DateTimeRange {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    end: now
  };
}

export function defaultCreationDateSort(): CreationDateSortOptions {
  return CreationDateSortOptions.CREATION_DATE_DESC;
}

export function defaultTimestampSort(): TimestampSortOptions {
  return TimestampSortOptions.TIMESTAMP_DATE_DESC;
}
